/**
 * Color System
 * Defines the color palette and utilities for the CABAL design system
 */

export enum ColorType {
  Accent = 'accent',
  Black = 'black',
  White = 'white',
  Transparent = 'transparent',
  Danger = 'danger',
  Warn = 'warn',
}

const COLOR_VALUE_BY_TYPE: Record<ColorType, string> = {
  [ColorType.Accent]: '#66FEC1',
  [ColorType.Black]: '#000000',
  [ColorType.White]: '#FFFFFF',
  [ColorType.Transparent]: 'transparent',
  [ColorType.Danger]: '#780000',
  [ColorType.Warn]: '#F29F57',
};

export const ColorValue = COLOR_VALUE_BY_TYPE;

/**
 * Get color value with optional opacity
 * @param color - ColorType enum value
 * @param opacity - Optional opacity value (0-1)
 * @returns CSS color string (hex or rgba)
 */
export function getColorValue(color: ColorType | string, opacity?: number): string {
  // If it's already a raw hex string (custom color), use it directly
  const hexColor = typeof color === 'string' && color.startsWith('#') 
    ? color.trim()
    : ColorValue[color as ColorType];
  
  if (opacity === undefined || opacity === 1 || color === ColorType.Transparent) {
    return hexColor;
  }
  
  // Convert hex to rgba
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Get accent color with opacity
 * Common helper for accent color variations
 */
export function getAccentColor(opacity: number = 1): string {
  return getColorValue(ColorType.Accent, opacity);
}

/**
 * Get black color with opacity
 * Common helper for background variations
 */
export function getBlackColor(opacity: number = 1): string {
  return getColorValue(ColorType.Black, opacity);
}
